import React, { useState, useEffect, useRef } from 'react';
import { fetchAll } from 'config/fetchAll';
import { submitIdempotentCreate } from 'config/idempotentCreate';
import { CLIENT_DELIVERIES_URL } from '../createClient';
import ClientInfo from './ClientInfo';
import TaskAssignment from './TaskAssignment';
import DriverAssignment from './DriverAssignment';
import { Task } from '../../../reusable/Client/dialog/AddTaskDialog';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSaveError } from 'reusable/useSaveError';

const AddClient: React.FC = () => {
  const { t } = useTranslation('client');
  const { showSaveError, showMessages, saveErrorPopup } = useSaveError();
  const [clientName, setClientName] = useState<string>('');
  const [clientDescription, setClientDescription] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [drivers, setDrivers] = useState<any[]>([]);
  const [selectedDrivers, setSelectedDrivers] = useState<number[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<Task[]>([]);
  const navigate = useNavigate();
  const [showErrorTooltip, setShowErrorTooltip] = useState(false);
  // EV-128。送信中は「作成」を押せない。state の反映より前の二重の送信（Enter の連打など）も
  // 止めるため、ref でも持つ。
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  // EV-128。結果が分からなかった作成の X-Request-ID と本文は、入力を変えずにもう一度押したら
  // 同じ値で送り、前回が確定していればサーバーのリプレイを受け取る（二重に作らない）。
  // RV-130。画面を離れても失わないよう、このコンポーネントではなくタブの sessionStorage に
  // 残す（config/idempotentCreate.ts の submitIdempotentCreate）。
  // RV-130。画面を離れた後に届いた結果では、移動や表示をしない（残す値の更新だけ行う）。
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Fetch drivers when search query changes
  useEffect(() => {
    // ADM-15。検索語が変わったら前の応答は捨てる（遅れて届いた古い結果で上書きしない）。
    let active = true;
    const fetchDrivers = async () => {
      try {
        // ADM-09。1ページ目（10件）だけでなく、検索に合う運転者を全件、候補にする。
        const driverData = await fetchAll<any>(
          `/v1/drivers/?search=${encodeURIComponent(searchQuery)}`
        );
        if (!active) return;
        setDrivers(Array.isArray(driverData) ? driverData : []);
      } catch (error) {
        if (!active) return;
        console.error('Failed to fetch drivers', error);
        setDrivers([]);
      }
    };

    fetchDrivers();
    return () => {
      active = false;
    };
  }, [searchQuery]);

  useEffect(() => {
    // ADM-15。追加と解除で同じ関数を渡す。以前は別の無名関数を渡しており、解除されずに残っていた。
    const closeTooltip = () => setShowErrorTooltip(false);
    document.addEventListener('click', closeTooltip);
    return () => {
      document.removeEventListener('click', closeTooltip);
    };
  }, [showErrorTooltip]);

  // RV-129。送信中（送り直し・処理中の問い合わせ直しを含む）は送る内容を変えさせない。
  // 画面の操作は disabled にし（fieldset と各部品の disabled）、それを通らない経路
  // （開いたままのダイアログなど）も、ここで変更を捨てる。送った本文と画面が食い違うと、
  // 結果が分からなかった作成を「そのまま」押し直しても新しい X-Request-ID になり、二重に作る。
  const whenEditable =
    <A extends unknown[]>(update: (...args: A) => void) =>
    (...args: A) => {
      if (!submittingRef.current) update(...args);
    };
  const editClientName = whenEditable(setClientName);
  const editClientDescription = whenEditable(setClientDescription);
  const editSelectedTasks = whenEditable(setSelectedTasks);
  const editSelectedDrivers = whenEditable(setSelectedDrivers);

  // Handle form submission to create a new client
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submittingRef.current) return;
    if (selectedTasks.length === 0) {
      setShowErrorTooltip(true);
      return;
    }
    const newClient = {
      name: clientName,
      description: clientDescription,
      // EV-139。運転者は選んだ順番に意味が無い（サーバーは集合として割り当てる）。ID の順に
      // そろえ、同じ運転者なら同じ本文（同じ X-Request-ID・サーバーの本文の照合も一致）にする。
      // 作業は並び順に意味があるので、そのまま。
      assign_drivers: [...selectedDrivers].sort((a, b) => a - b),
      tasks: selectedTasks.map((task) => task.id),
    };
    const tasksSnapshot = selectedTasks;
    // RV-129。結果が分からず値を残すときは、画面を送った内容（保存した本文）に戻す。変更は
    // 送信中に止めているが、念のため、画面に見えている内容と送り直す内容を必ず一致させる。
    const restoreSubmitted = () => {
      setClientName(newClient.name);
      setClientDescription(newClient.description);
      setSelectedTasks(tasksSnapshot);
      setSelectedDrivers(newClient.assign_drivers);
    };
    submittingRef.current = true;
    setSubmitting(true);
    try {
      // EV-128（ADM-14 の AC1）。作成の操作ごとに X-Request-ID を作り、結果が分からない失敗では
      // 同じ値・同じ本文で送り直す。前回の結果が分からず、入力も変わっていなければ前回と同じ
      // 値を使う。期限（RV-127）・前回の値を残す条件（RV-128）・タブに残す（RV-130）・自分の値
      // だけを消す（EV-138）・送るときの利用者の印（EV-140）も含め、作業マスタ・機器・車両の
      // 作成と共通（config/idempotentCreate.ts の submitIdempotentCreate）。
      const outcome = await submitIdempotentCreate('client', CLIENT_DELIVERIES_URL, newClient);
      if (outcome.status === 'created') {
        if (mounted.current) navigate('/client');
      } else if (outcome.status === 'expired') {
        // RV-127。期限が近く、前回の値は使えない。古い値で黙って送らず、一覧を読み直して
        // 警告する。警告の後にもう一度押したときだけ、新しい値で作る。
        const messages = await messagesWithListCheck(t('ui.create-retry-expired'), newClient.name);
        if (mounted.current) showMessages(messages);
      } else if (outcome.status === 'unknown') {
        // 作成できたかを確かめられなかった（値は残した）。入力も残し、配送先の一覧を読み直す。
        console.error('Failed to add client', outcome.error);
        if (mounted.current) {
          restoreSubmitted();
          const messages = await messagesWithListCheck(
            t('ui.create-outcome-unknown'),
            newClient.name
          );
          if (mounted.current) showMessages(messages);
        }
      } else {
        console.error('Failed to add client', outcome.error);
        // RV-128。前回の値を残したとき（X-Request-ID の処理より前に断られた）は、画面を
        // 送った内容に戻す。
        if (outcome.keptRecord && mounted.current) restoreSubmitted();
        if (mounted.current) showSaveError(outcome.error);
      }
    } finally {
      submittingRef.current = false;
      if (mounted.current) setSubmitting(false);
    }
  };

  // EV-128。一覧を読み直し、同じ名前の配送先があれば、作成済みでないかを確かめるよう添える。
  // 名前の一致は「作成できた」証拠にしない（前からある同じ名前の配送先かもしれないため）。
  // RV-127。期限が近く同じ値を使えないときの警告（lead）にも使う。
  const messagesWithListCheck = async (lead: string, name: string): Promise<string[]> => {
    const messages = [lead];
    try {
      const clients = await fetchAll<any>('/v1/client_deliveries/');
      if (clients.some((client) => client?.name === name)) {
        messages.push(t('ui.create-same-name-exists'));
      }
    } catch (error) {
      console.error('Failed to reload clients', error);
    }
    return messages;
  };

  return (
    <section className="bg-white rounded-md m-4">
      <form onSubmit={handleSubmit}>
        {/* RV-129。送信中は入力・作業・運転者の操作をまとめて無効にする。 */}
        <fieldset disabled={submitting} className="min-w-0">
          <ClientInfo
            clientName={clientName}
            setClientName={editClientName}
            clientDescription={clientDescription}
            setClientDescription={editClientDescription}
          />

          <hr />

          <TaskAssignment
            selectedTasks={selectedTasks}
            setSelectedTasks={editSelectedTasks}
            showErrorTooltip={showErrorTooltip}
            setShowErrorTooltip={() => setShowErrorTooltip(false)}
            disabled={submitting}
          />

          <hr />

          <DriverAssignment
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            drivers={drivers}
            selectedDrivers={selectedDrivers}
            handleDriverSelect={(driver) =>
              editSelectedDrivers((prev) => [...prev, driver.id])
            }
            handleDriverRemove={(driverId) =>
              editSelectedDrivers((prev) => prev.filter((id) => id !== driverId))
            }
            disabled={submitting}
          />
        </fieldset>
        <div className="flex justify-end mt-4 gap-4 border-t p-4">
          <button
            type="button"
            // RV-130。送信中は画面を離れさせない（離れると結果を受け取れない。値は残る）。
            disabled={submitting}
            className="bg-gray-200 font-semi-bold text-subtitle-2 text-primary/300 h-12 py-2 px-20 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => navigate('/client')}
          >
            {t('action.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            aria-busy={submitting}
            className="bg-primary/300 font-medium text-subtitle-2 text-white h-12 py-2 px-12 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('action.create_client')}
          </button>
        </div>
      </form>
      {saveErrorPopup}
    </section>
  );
};

export default AddClient;
